import { Truck, Clock, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';

export function ShippingPolicy() {
  usePageSEO({
    title: 'Shipping & Delivery Policy | WellZora Herbal Care Pakistan',
    description: 'Find complete details on WellZora Herbal Care shipping timelines, Cash on Delivery (COD) services, courier partners, and free delivery thresholds across Pakistan.',
    canonical: 'https://wellzoraherbalcare.com/shipping-policy',
  });

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 text-primary rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <Truck className="w-3.5 h-3.5" /> Fast Nationwide Shipping
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            Shipping &amp; Delivery Policy
          </h1>
          <p className="text-gray-500 text-sm mb-8 pb-6 border-b border-gray-100">
            Last Updated: September 2026 • Cash on Delivery Across Pakistan
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" /> 1. Delivery Timelines
              </h2>
              <p className="mb-3">
                Orders are processed and dispatched within 24 business hours from our central distribution laboratory in Lahore, Pakistan:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">Lahore, Karachi, Islamabad &amp; Rawalpindi</h3>
                  <p className="text-xs text-gray-600">Delivered within <strong>2 to 3 working days</strong>.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">Other Cities, Towns &amp; Rural Tehsils</h3>
                  <p className="text-xs text-gray-600">Delivered within <strong>3 to 5 working days</strong>.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" /> 2. Shipping Charges &amp; Free Delivery
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
                <li>
                  <strong>FREE Delivery:</strong> Applicable on all single and package orders with a subtotal of <strong>Rs. 3,000 or above</strong>.
                </li>
                <li>
                  <strong>Standard Delivery:</strong> A flat rate of Rs. 200–250 is applied for smaller orders below Rs. 3,000 to cover courier handling.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> 3. Cash on Delivery (COD) Procedure
              </h2>
              <p className="mb-2">
                We operate on a 100% Cash on Delivery basis. You do not need to pay in advance:
              </p>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-gray-600">
                <li>Once you place an order online or via WhatsApp, our confirmation representative may contact you via SMS or call.</li>
                <li>The parcel is dispatched with an official tracking code provided to your mobile number.</li>
                <li>Upon arrival, hand over the exact cash amount to the delivery rider and receive your confidential sealed package.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary" /> 4. Damaged or Tampered Parcels
              </h2>
              <p className="text-sm text-gray-600">
                If the courier flyer appears visibly torn or damaged prior to opening, please refuse to accept it and immediately alert our WhatsApp team at <strong>0305-6367673</strong> so we can immediately dispatch a fresh replacement package at no additional cost.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
